import { motion } from "framer-motion";
import { BarChart3, FileText, Download, TrendingUp, Calendar, Tag, ExternalLink, Database, Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import FeaturedSpotlight from "@/components/insights/FeaturedSpotlight";
import CommunityInsightsStrip from "@/components/insights/CommunityInsightsStrip";


import LegislationNews from "@/components/LegislationNews";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";
import { useCuratedPosts } from "@/hooks/useCuratedPosts";
import { useState } from "react";
import TrendStatsRow from "@/components/insights/TrendStatsRow";
import TrendFilters from "@/components/insights/TrendFilters";
import type { Timeframe } from "@/hooks/useTrendTopics";
import { matchFocusAreas, type FocusArea } from "@/lib/focusAreas";
import SaveButton from "@/components/saved/SaveButton";
import baldwinPortraitAsset from "@/assets/baldwin-portrait-allan-warren.jpg.asset.json";
import { CheckCircle2, MapPin as MapPinIcon, Lightbulb } from "lucide-react";

const outcomeStyles: Record<string, string> = {
  win: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  partial: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  loss: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  pending: "bg-muted text-muted-foreground border-border",
};

const CaseStudiesRail = () => {
  const { data: studies = [] } = useQuery({
    queryKey: ["insights-case-studies-rail"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_case_studies");
      if (error) throw error;
      return (data ?? []).slice(0, 3);
    },
  });

  if (!studies.length) return null;

  return (
    <section className="py-20 border-t border-border/60">
      <div className="container mx-auto px-6">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Field Notes</span>
            <h2 className="font-display text-3xl font-bold mt-2">Case Studies from the Field</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Outcomes and lessons captured directly from community-led projects.
            </p>
          </div>
          <Link
            to="/archive/case-studies"
            className="text-sm text-primary font-semibold inline-flex items-center group shrink-0"
          >
            See all case studies
            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {studies.map((s: any, i: number) => {
            const outcome = (s.latest_outcome ?? "pending") as string;
            return (
              <motion.div
                key={s.project_id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 + i * 0.05 }}
              >
                <Link
                  to={`/archive/case-studies/${s.project_id}`}
                  className="group block bg-card border border-border rounded-2xl p-6 h-full hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${outcomeStyles[outcome] ?? outcomeStyles.pending}`}
                    >
                      {outcome}
                    </span>
                    {s.idea_category && (
                      <span className="text-xs text-muted-foreground">{s.idea_category}</span>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors leading-snug">
                    {s.idea_title}
                  </h3>
                  {s.latest_summary && (
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                      <Lightbulb className="inline h-3.5 w-3.5 mr-1 text-primary/70" />
                      {s.latest_summary}
                    </p>
                  )}
                  <div className="text-xs text-muted-foreground flex items-center gap-3 border-t border-border pt-3 mt-auto">
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {s.public_lessons_count} lesson{s.public_lessons_count === 1 ? "" : "s"}
                    </span>
                    {s.author_display_name && (
                      <span className="truncate">{s.author_display_name}</span>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const baldwinPortrait = baldwinPortraitAsset.url;

const fallbackArticles = [
  {
    slug: null as string | null,
    tag: "Policy Analysis",
    title: "How Alabama's HB-247 Could Reshape Rural Healthcare Access",
    excerpt: "A deep dive into the implications of the latest healthcare bill and what it means for underserved counties across the state.",
    date: "Mar 25, 2026",
    readTime: "8 min read",
    image_url: null as string | null,
  },
  {
    slug: null as string | null,
    tag: "Funding Trends",
    title: "Federal Grant Landscape: What Changed in Q1 2026",
    excerpt: "An overview of shifting priorities in federal funding and new opportunities for community-based organizations.",
    date: "Mar 18, 2026",
    readTime: "6 min read",
    image_url: null as string | null,
  },
  {
    slug: null as string | null,
    tag: "Impact Story",
    title: "From Data to Action: How Shelby County Secured $2.3M in Education Grants",
    excerpt: "A case study on how one county leveraged The Emerging Market's tools to align their programs with available funding.",
    date: "Mar 10, 2026",
    readTime: "5 min read",
    image_url: null as string | null,
  },
];

const dataHighlights = [
  { label: "Grants Tracked This Month", value: "142", change: "+23%", icon: TrendingUp },
  { label: "Active Legislation", value: "67", change: "+8", icon: FileText },
  { label: "Avg. Grant Size", value: "$385K", change: "+12%", icon: BarChart3 },
  { label: "Partner Matches", value: "54", change: "+31%", icon: Tag },
];

const resources = [
  {
    title: "Financial Empowerment Report & Action Plan — City of Mobile",
    description: "A comprehensive blueprint from the City of Mobile addressing financial empowerment challenges, community outreach findings, and actionable strategies for expanding economic opportunity across all neighborhoods.",
    format: "PDF",
    size: "1.7 MB",
    url: "https://ypfplfhmimgwjtvlswpw.supabase.co/storage/v1/object/public/reports/financial-empowerment-report-mobile-al.pdf",
    citation: "https://www.cityofmobile.gov",
  },
  {
    title: "2026 Alabama Grants Directory",
    description: "Comprehensive listing of federal, state, and philanthropic funding opportunities relevant to Alabama communities.",
    format: "PDF",
    size: "2.4 MB",
  },
  {
    title: "Nonprofit Funding Alignment Guide",
    description: "Step-by-step framework for matching your organization's programs to available grant opportunities.",
    format: "PDF",
    size: "1.8 MB",
  },
  {
    title: "Legislative Impact Scorecard – Q1 2026",
    description: "Quarterly analysis of bills passed, pending, and their projected community impact across key issue areas.",
    format: "PDF",
    size: "3.1 MB",
  },
];

const Insights = () => {
  const { blocks: copy } = useSiteContent("insights");
  const [timeframe, setTimeframe] = useState<Timeframe>("7d");
  const [focus, setFocus] = useState<FocusArea | "all">("all");
  const pageIntro = getBlock(
    copy,
    "page_intro",
    "The editorial layer above the Archive and Community Profiles — policy analysis, funding signals, and case studies rooted in specific Alabama places.",
  );
  const { data: dbArticles } = useQuery({
    queryKey: ["published-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: curatedPosts = [] } = useCuratedPosts();

  const articles = (dbArticles && dbArticles.length > 0)
    ? dbArticles.map((a) => ({
        slug: a.slug,
        tag: a.category,
        title: a.title,
        excerpt: a.excerpt || "",
        date: a.published_at ? format(new Date(a.published_at), "MMM d, yyyy") : "",
        readTime: `${Math.max(1, Math.ceil((a.body?.length || 0) / 1000))} min read`,
        image_url: a.image_url,
      }))
    : fallbackArticles;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(135 30% 45%) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Insights</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5">
              Analysis, Data & <span className="text-gradient-primary">Resources</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {pageIntro}
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              <Link
                to="/archive"
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-1.5 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors"
              >
                Browse the full Archive <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/communities"
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-1.5 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors"
              >
                <MapPinIcon className="h-3.5 w-3.5" /> Explore Community Profiles
              </Link>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Featured Spotlight */}
      <FeaturedSpotlight />

      {/* Insights by Community */}
      <CommunityInsightsStrip />

      {/* Trend Stats */}
      <TrendStatsRow timeframe={timeframe} />

      {/* Articles — filter out duplicates already shown in Featured Spotlight */}
      {(() => {
        const featuredSlugs = new Set([
          "mobile-financial-empowerment-action-plan-analysis",
          "mobile-priority-zip-codes-where-home-values-stay-below-50k",
          "52-percent-black-population-business-ownership-gap",
          "mobile-unbanked-underbanked-banking-access-gap",
        ]);
        const remaining = articles.filter((a) => a.slug && !featuredSlugs.has(a.slug));
        const baldwin = remaining.find((a) => a.slug === "james-baldwin-witness-mobile-alabama");
        const others = remaining.filter((a) => a.slug !== "james-baldwin-witness-mobile-alabama");

        type MixedItem =
          | {
              kind: "article";
              key: string;
              href: string;
              title: string;
              excerpt: string;
              tag: string | null;
              date: string;
              dateSort: number;
              readTime: string;
              image_url: string | null;
            }
          | {
              kind: "curated";
              key: string;
              href: string;
              title: string;
              excerpt: string;
              tag: string | null;
              date: string;
              dateSort: number;
              image_url: string | null;
              source_org: string;
              source_url: string;
            };

        const articleItems: MixedItem[] = others.map((a) => ({
          kind: "article",
          key: `article-${a.slug ?? a.title}`,
          href: a.slug ? `/insights/${a.slug}` : "#",
          title: a.title,
          excerpt: a.excerpt,
          tag: a.tag,
          date: a.date,
          dateSort: a.date ? +new Date(a.date) : 0,
          readTime: a.readTime,
          image_url: a.image_url,
        }));
        const curatedItems: MixedItem[] = curatedPosts.map((c) => ({
          kind: "curated",
          key: `curated-${c.id}`,
          href: `/archive/curated/${c.slug}`,
          title: c.title,
          excerpt: c.summary ?? "",
          tag: c.category,
          date: c.published_at
            ? format(new Date(c.published_at), "MMM d, yyyy")
            : format(new Date(c.created_at), "MMM d, yyyy"),
          dateSort: +new Date(c.published_at ?? c.created_at),
          image_url: c.cover_image_url,
          source_org: c.source_org_name,
          source_url: c.source_url,
        }));
        const mixedItemsAll = [...articleItems, ...curatedItems].sort(
          (a, b) => b.dateSort - a.dateSort,
        );
        const mixedItems = focus === "all"
          ? mixedItemsAll
          : mixedItemsAll.filter((it) => {
              const haystack = `${it.tag ?? ""} ${it.title} ${it.excerpt}`;
              return matchFocusAreas(haystack).includes(focus);
            });

        return (
          <section className="py-20">
            <div className="container mx-auto px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <span className="text-primary text-sm font-semibold tracking-wider uppercase">Happening Now</span>
                  <h2 className="font-display text-3xl font-bold mt-2">Trends and Social Signals</h2>
                </div>
                <Link
                  to="/archive"
                  className="text-sm text-primary font-semibold inline-flex items-center group shrink-0"
                >
                  View all in Archive
                  <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <TrendFilters
                focus={focus}
                onFocusChange={setFocus}
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
              />

              {baldwin && (
                <Link to={`/insights/${baldwin.slug}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-colors cursor-pointer mb-8"
                  >
                    <div className="grid lg:grid-cols-5 gap-0">
                      <div className="lg:col-span-2 relative overflow-hidden bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 h-[260px] lg:h-full">
                        <img
                          src={baldwinPortrait}
                          alt="James Baldwin photographed by Allan Warren in Hyde Park, London, 1969"
                          width={1024}
                          height={1280}
                          className="w-full h-full object-cover"
                          loading="eager"
                        />
                      </div>
                      <div className="lg:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                            {baldwin.tag}
                          </span>
                          <span className="text-xs text-muted-foreground">{baldwin.readTime}</span>
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors leading-snug">
                          {baldwin.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed mb-6 max-w-xl">
                          James Baldwin (1924–1987) was a novelist, essayist, and public witness whose work named what most of his country preferred not to see — the shape of American racism, the cost of denial, and the unfinished business of love. We invoke him at the top of Pride Month not as decoration, but because his posture is the posture our firm tries to keep: tell the truth, stay in the room, and represent the people whose lives are being decided about.
                        </p>
                        <div className="flex items-center gap-4">
                          <Button variant="link" className="px-0 text-primary font-semibold group/btn" asChild>
                            <span>
                              Read Full Tribute
                              <ArrowRight className="ml-1.5 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                            </span>
                          </Button>
                          <span className="text-xs text-muted-foreground">{baldwin.date}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              )}

              {mixedItems.length > 0 && (
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
                  {mixedItems.map((item, i) => {
                    const slugOrId =
                      item.kind === "article"
                        ? item.href.replace(/^\/insights\//, "")
                        : item.key.replace(/^curated-/, "");
                    return (
                      <div key={item.key} className="relative break-inside-avoid mb-6 group/tile">
                        <Link to={item.href} className="block">
                          <motion.article
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(0.05 + i * 0.04, 0.4) }}
                        className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-colors cursor-pointer"
                      >
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.title}
                            loading="lazy"
                            className="w-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                          />
                        ) : (
                          <div className="aspect-[4/3] bg-gradient-to-br from-primary/10 via-accent/5 to-transparent flex items-center justify-center">
                            <FileText className="h-12 w-12 text-primary/30 group-hover:text-primary/50 transition-colors" />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex items-center gap-2 flex-wrap mb-3">
                            {item.kind === "curated" && (
                              <span className="text-xs font-medium bg-accent/15 text-accent-foreground border border-accent/30 px-2.5 py-1 rounded-full">
                                EM Curated
                              </span>
                            )}
                            {item.tag && (
                              <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                                {item.tag}
                              </span>
                            )}
                            {item.kind === "article" && (
                              <span className="text-xs text-muted-foreground">{item.readTime}</span>
                            )}
                          </div>
                          <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors leading-snug">
                            {item.title}
                          </h3>
                          {item.excerpt && (
                            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                              {item.excerpt}
                            </p>
                          )}
                          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span className="inline-flex items-center">
                              <Calendar className="h-3.5 w-3.5 mr-1.5" />
                              {item.date}
                            </span>
                            {item.kind === "curated" && (
                              <span className="inline-flex items-center gap-1 truncate">
                                <ExternalLink className="h-3 w-3 shrink-0" />
                                <span className="truncate">From {item.source_org}</span>
                              </span>
                            )}
                          </div>
                        </div>
                          </motion.article>
                        </Link>
                        <div className="absolute top-3 right-3 z-10">
                          <SaveButton
                            variant="icon"
                            item={{
                              item_type: item.kind === "article" ? "article" : "curated_post",
                              item_id: slugOrId,
                              title: item.title,
                              url: item.href,
                              source: item.kind === "curated" ? item.source_org : "Insights",
                              cover_image_url: item.image_url,
                              summary: item.excerpt,
                              metadata: { tag: item.tag, date: item.date },
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        );
      })()}


      {/* Legislation in the news */}
      <LegislationNews />

      {/* Case Studies from the Field */}
      <CaseStudiesRail />

      {/* Reports & Resources */}
      <section className="py-20 bg-muted/20 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="mb-10">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Utilities</span>
            <h2 className="font-display text-3xl font-bold mt-2">Tools & Downloads</h2>
          </div>

          <div className="space-y-4 max-w-3xl">
            {resources.map((resource, i) => (
              <motion.a
                key={resource.title}
                href={"url" in resource ? (resource as any).url : "#"}
                target="_blank"
                rel="noopener noreferrer"
                download
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.1 }}
                className="bg-card border border-border rounded-xl p-6 flex items-start gap-5 hover:border-primary/30 transition-colors group block"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Download className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    {resource.description}
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="bg-muted px-2 py-0.5 rounded">{resource.format}</span>
                    <span>{resource.size}</span>
                    {"citation" in resource && (
                      <a
                        href={(resource as any).citation}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        Source: cityofmobile.gov
                      </a>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-sm font-medium mt-2">
                  <Download className="h-4 w-4" />
                </div>
              </motion.a>
            ))}
          </div>

          {/* Data Tools */}
          <div className="mt-14">
            <h3 className="font-display text-xl font-bold mb-4">Data Tools</h3>
            <div className="space-y-4">
              <motion.a
                href="/data-tools"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card border border-border rounded-xl p-6 flex items-start gap-5 hover:border-primary/30 transition-colors group block"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    Alabama Community Data Explorer
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Explore county-level data on education, workforce, financial well-being, and health across all 67 Alabama counties.
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="bg-muted px-2 py-0.5 rounded">Interactive</span>
                    <span>Live Census Data</span>
                  </div>
                </div>
                <div className="shrink-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-sm font-medium mt-2">
                  Explore <ExternalLink className="h-3.5 w-3.5" />
                </div>
              </motion.a>
              <motion.a
                href="https://mobile-county-insights.lovable.app"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-card border border-border rounded-xl p-6 flex items-start gap-5 hover:border-primary/30 transition-colors group block"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    Mobile County Insights
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Explore property values, land transactions, demographic trends, and housing data for Mobile County, Alabama.
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="bg-muted px-2 py-0.5 rounded">Interactive</span>
                    <span>County-level data</span>
                  </div>
                </div>
                <div className="shrink-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-sm font-medium mt-2">
                  Open <ExternalLink className="h-3.5 w-3.5" />
                </div>
              </motion.a>
            </div>
          </div>
        </div>
      </section>


      <SiteFooter />
    </div>
  );
};

export default Insights;
